import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { Package, ShoppingCart, MapPin, AlertCircle, CheckCircle, Clock, Truck, CreditCard, Minus, Plus, Bell } from 'lucide-react';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Card } from './components/ui/card';
import { Label } from './components/ui/label';

export default function ReaderOrders({ readerData, currentReader, authorId, bookId, promoApplied }) {
  console.log('[ReaderOrders] promoApplied prop:', promoApplied);
  const [myOrders, setMyOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  // Multi-select: { [productId]: quantity }
  const [selectedItems, setSelectedItems] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState({}); // { [productId]: 'on' | 'joining' }

  // Load waitlist status for current reader
  useEffect(() => {
    if (!currentReader || !authorId) return;
    const unsub = onSnapshot(
      query(
        collection(db, 'authors', authorId, 'waitlist'),
        where('email', '==', currentReader.email)
      ),
      (snap) => {
        const status = {};
        snap.forEach(d => {
          const data = d.data();
          if (data.productId) status[data.productId] = 'on';
        });
        setWaitlistStatus(status);
      }
    );
    return unsub;
  }, [currentReader, authorId]);

  const joinWaitlist = async (product) => {
    if (!currentReader || !authorId || waitlistStatus[product.id]) return;
    setWaitlistStatus(prev => ({ ...prev, [product.id]: 'joining' }));
    try {
      const waitlistDocId = `${currentReader.uid}_${product.id}`;
      await setDoc(doc(db, 'authors', authorId, 'waitlist', waitlistDocId), {
        readerId: currentReader.uid,
        name: readerData?.name || currentReader.displayName || currentReader.email,
        email: currentReader.email,
        productId: product.id,
        productName: product.name,
        bookId: bookId || null,
        timestamp: serverTimestamp(),
      });
      setWaitlistStatus(prev => ({ ...prev, [product.id]: 'on' }));
    } catch (err) {
      console.error('Waitlist join error:', err);
      setWaitlistStatus(prev => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });
    }
  };

  // Load products from author/book tenant path
  useEffect(() => {
    if (!authorId || !bookId) {
      setIsLoadingProducts(false);
      return;
    }
    const unsub = onSnapshot(
      collection(db, 'authors', authorId, 'books', bookId, 'products'),
      (snap) => {
        const items = [];
        snap.forEach(d => {
          const data = d.data();
          if (data.available) items.push({ id: d.id, ...data });
        });
        setProducts(items);
        setIsLoadingProducts(false);
      },
      (err) => {
        console.error('Products listener error:', err);
        setIsLoadingProducts(false);
      }
    );
    return unsub;
  }, [authorId, bookId]);

  // Load reader's orders from author tenant path
  useEffect(() => {
    if (!currentReader || !authorId) return;
    const q = query(
      collection(db, 'authors', authorId, 'orders'),
      where('readerEmail', '==', currentReader.email)
    );
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setMyOrders(items);
    });
    return unsub;
  }, [currentReader, authorId]);

  const getShippingCost = (product) => {
    if (!product || !readerData?.shippingAddress?.country) return 0;
    const country = readerData.shippingAddress.country.toLowerCase();
    const UK = ['uk', 'gb', 'united kingdom', 'england', 'scotland', 'wales', 'northern ireland', 'great britain'];
    const EUROPE = ['austria', 'belgium', 'bulgaria', 'croatia', 'cyprus', 'czech', 'denmark', 'estonia', 'finland',
      'france', 'germany', 'greece', 'hungary', 'iceland', 'ireland', 'italy', 'latvia', 'lithuania', 'luxembourg',
      'malta', 'netherlands', 'norway', 'poland', 'portugal', 'romania', 'slovakia', 'slovenia', 'spain', 'sweden',
      'switzerland', 'turkey', 'ukraine', 'serbia', 'bosnia', 'montenegro', 'albania', 'macedonia', 'moldova'];
    const US_CANADA = ['us', 'usa', 'united states', 'america', 'canada', 'ca'];
    if (UK.some(c => country.includes(c))) return product.shippingUK ?? product.shippingDomestic ?? 3.50;
    if (EUROPE.some(c => country.includes(c))) return product.shippingEurope ?? product.shippingInternational ?? 8.00;
    if (US_CANADA.some(c => country.includes(c))) return product.shippingUSCanada ?? 12.00;
    return product.shippingRestOfWorld ?? product.shippingInternational ?? 14.00;
  };

  const hasCompleteAddress = () => {
    if (!readerData?.shippingAddress) return false;
    const addr = readerData.shippingAddress;
    return addr.line1 && addr.city && addr.postalCode && addr.country;
  };

  const isSoldOut = (product) =>
    product.stockQuantity != null && product.stockQuantity !== "" && Number(product.stockQuantity) <= 0;

  const toggleProduct = (productId) => {
    setSelectedItems(prev => {
      if (prev[productId]) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return { ...prev, [productId]: 1 };
    });
  };

  const setQuantity = (productId, qty) => {
    const clamped = Math.max(1, Math.min(10, qty));
    setSelectedItems(prev => ({ ...prev, [productId]: clamped }));
  };

  const hasSelection = Object.keys(selectedItems).length > 0;

  // Calculate order totals
  const getOrderTotals = () => {
    let subtotal = 0;
    let totalShipping = 0;
    const itemDetails = [];
    for (const [productId, qty] of Object.entries(selectedItems)) {
      const product = products.find(p => p.id === productId);
      if (!product) continue;
      const shipping = getShippingCost(product);
      subtotal += product.price * qty;
      totalShipping += shipping * qty;
      itemDetails.push({ product, qty, shipping });
    }
    return { subtotal, totalShipping, grandTotal: subtotal + totalShipping, itemDetails };
  };

  const handleCheckout = async () => {
    if (!hasSelection || !hasCompleteAddress()) return;
    setIsSubmitting(true);
    try {
      const items = Object.entries(selectedItems).map(([productId, quantity]) => ({
        productId,
        bookId,
        quantity,
      }));
      const createOrderCheckout = httpsCallable(functions, 'createOrderCheckout');
      const payload = {
        items,
        bookId,
        authorId,
        shippingAddress: readerData.shippingAddress,
        readerName: readerData.name || currentReader.email,
        ...(promoApplied ? {
          promoCodeId: promoApplied.id,
          promoCode: promoApplied.code,
          freeShipping: !!promoApplied.freeShipping,
        } : {}),
      };
      console.log('[ReaderOrders] createOrderCheckout payload:', payload);
      const result = await createOrderCheckout(payload);
      // Redirect to Stripe Checkout
      if (result.data?.sessionUrl) {
        window.location.href = result.data.sessionUrl;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Error starting checkout: ' + (err.message || 'Please try again'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      Pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending', icon: Clock },
      Processing: { color: 'bg-blue-100 text-blue-700', label: 'Processing', icon: Package },
      Shipped: { color: 'bg-purple-100 text-purple-700', label: 'Shipped', icon: Truck },
      Completed: { color: 'bg-green-100 text-green-700', label: 'Completed', icon: CheckCircle },
      Cancelled: { color: 'bg-red-100 text-red-700', label: 'Cancelled', icon: AlertCircle },
      // Legacy status mapping
      pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending', icon: Clock },
      payment: { color: 'bg-yellow-100 text-yellow-700', label: 'Awaiting Payment', icon: CreditCard },
      paid: { color: 'bg-blue-100 text-blue-700', label: 'Paid', icon: CheckCircle },
      processing: { color: 'bg-blue-100 text-blue-700', label: 'Processing', icon: Package },
      shipped: { color: 'bg-purple-100 text-purple-700', label: 'Shipped', icon: Truck },
      delivered: { color: 'bg-green-100 text-green-700', label: 'Delivered', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-700', label: 'Cancelled', icon: AlertCircle },
    };
    const badge = badges[status] || badges.Pending;
    const Icon = badge.icon;
    return (
      <Badge className={badge.color}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </Badge>
    );
  };

  if (isLoadingProducts) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Package className="mx-auto h-12 w-12 animate-pulse text-teal-600" />
          <p className="mt-2 text-slate-600">Loading shop...</p>
        </div>
      </div>
    );
  }

  const { subtotal, totalShipping, grandTotal, itemDetails } = getOrderTotals();

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Orders</h2>
          <p className="text-sm text-slate-600">Order signed books and special edition boxes</p>
        </div>
      </div>

      {/* No products available */}
      {products.length === 0 && (
        <Card className="p-8 text-center">
          <Package className="mx-auto mb-3 h-12 w-12 text-slate-300" />
          <p className="text-slate-500">No products available for this book yet.</p>
        </Card>
      )}

      {/* Address Warning */}
      {!hasCompleteAddress() && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-slate-900">Shipping Address Required</p>
              <p className="mt-1 text-sm text-teal-700">
                Please complete your shipping address in your profile before placing an order.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Order Form — always visible */}
      {products.length > 0 && (
        <Card className="rounded-2xl p-6 shadow-lg">
          <h3 className="mb-6 text-xl font-bold text-slate-900">Choose your copy</h3>

          {/* Product Selection */}
          <div className="space-y-6">
            <div>
              <Label className="mb-3 block">Select Products *</Label>
              <div className="space-y-3">
                {products.map((product) => {
                  const shipping = getShippingCost(product);
                  const soldOut = isSoldOut(product);
                  const isSelected = !!selectedItems[product.id];
                  const qty = selectedItems[product.id] || 1;
                  const lineTotal = (product.price + shipping) * qty;
                  return (
                    <div
                      key={product.id}
                      className={`rounded-lg border-2 p-4 transition-all ${
                        soldOut
                          ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                          : isSelected
                            ? 'border-slate-500 bg-slate-50'
                            : 'border-slate-200 bg-white hover:border-teal-300'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => !soldOut && toggleProduct(product.id)}
                          disabled={soldOut}
                          className="mt-1 h-5 w-5 rounded border-slate-300 text-teal-600 cursor-pointer"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-slate-900">{product.name}</p>
                            {soldOut ? (
                              <Badge className="bg-red-100 text-red-700">Sold Out</Badge>
                            ) : (
                              <p className="text-xl font-bold text-teal-600">£{product.price.toFixed(2)}</p>
                            )}
                          </div>
                          {product.description && (
                            <p className="mt-1 text-sm text-slate-600">{product.description}</p>
                          )}
                          {soldOut && (
                            <div className="mt-2">
                              {waitlistStatus[product.id] === 'on' ? (
                                <p className="flex items-center gap-1.5 text-xs font-medium text-teal-700">
                                  <CheckCircle className="h-3.5 w-3.5" />
                                  You're on the waitlist — we'll notify you when it's back in stock
                                </p>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => joinWaitlist(product)}
                                  disabled={waitlistStatus[product.id] === 'joining'}
                                  className="text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
                                >
                                  <Bell className="mr-1.5 h-3.5 w-3.5" />
                                  {waitlistStatus[product.id] === 'joining' ? 'Joining...' : 'Notify me when available'}
                                </Button>
                              )}
                            </div>
                          )}
                          {!soldOut && (
                            <p className="mt-1 text-xs text-slate-400">
                              {product.productType} · + £{shipping.toFixed(2)} shipping per item
                            </p>
                          )}
                        </div>
                      </div>
                      {/* Quantity selector — only when selected */}
                      {isSelected && !soldOut && (
                        <div className="mt-3 ml-9 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-600">Qty:</span>
                            <div className="flex items-center rounded-lg border border-slate-200">
                              <button
                                type="button"
                                onClick={() => setQuantity(product.id, qty - 1)}
                                disabled={qty <= 1}
                                className="px-2 py-1 text-slate-500 hover:text-slate-700 disabled:opacity-30"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={qty}
                                onChange={e => setQuantity(product.id, parseInt(e.target.value) || 1)}
                                className="w-12 border-x border-slate-200 text-center text-sm py-1 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                              <button
                                type="button"
                                onClick={() => setQuantity(product.id, qty + 1)}
                                disabled={qty >= 10}
                                className="px-2 py-1 text-slate-500 hover:text-slate-700 disabled:opacity-30"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm font-semibold text-slate-700">
                            Line total: £{lineTotal.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Shipping Address Preview */}
            {hasCompleteAddress() && (
              <Card className="rounded-lg bg-slate-50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-700">Shipping To:</p>
                </div>
                <div className="text-sm text-slate-600">
                  <p>{readerData.shippingAddress.line1}</p>
                  {readerData.shippingAddress.line2 && <p>{readerData.shippingAddress.line2}</p>}
                  <p>
                    {readerData.shippingAddress.city}, {readerData.shippingAddress.postalCode}
                  </p>
                  <p className="font-medium">{readerData.shippingAddress.country}</p>
                </div>
              </Card>
            )}

            {/* Order Summary */}
            {hasSelection && (
              <Card className="rounded-lg border-slate-200 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/40 p-4">
                <h4 className="mb-3 text-sm font-semibold text-slate-700">Order Summary</h4>
                <div className="space-y-2 text-sm">
                  {itemDetails.map(({ product, qty, shipping }) => (
                    <div key={product.id}>
                      <div className="flex justify-between">
                        <span className="text-slate-600">
                          {product.name} × {qty}
                        </span>
                        <span className="font-semibold text-slate-900">£{(product.price * qty).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-slate-400">
                        <span>Shipping × {qty}</span>
                        <span>£{(shipping * qty).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-slate-200 pt-2">
                    <span className="text-slate-600">Products</span>
                    <span className="font-semibold text-slate-900">£{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Shipping</span>
                    <span className="font-semibold text-slate-900">£{totalShipping.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-2">
                    <span className="font-bold text-slate-900">Total</span>
                    <span className="text-xl font-bold text-teal-600">
                      £{grandTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            {/* Info Note */}
            <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-800">
              <p className="font-medium mb-1">Secure Checkout</p>
              <p>You'll be redirected to Stripe to complete your payment securely by card.</p>
            </div>

            {/* Duplicate address warning above checkout button */}
            {!hasCompleteAddress() && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-teal-600 shrink-0" />
                  <p className="text-sm text-teal-700">
                    Please complete your shipping address in your profile before placing an order.
                  </p>
                </div>
              </div>
            )}

            {/* Action Button */}
            <Button
              onClick={handleCheckout}
              disabled={!hasSelection || !hasCompleteAddress() || isSubmitting}
              className="w-full bg-gradient-to-r from-slate-500 to-teal-600 px-6 py-3 font-semibold text-white transition-all hover:from-teal-600 hover:to-teal-700"
            >
              <CreditCard className="mr-2 h-5 w-5" />
              {isSubmitting ? 'Redirecting to Stripe...' : `Pay £${grandTotal.toFixed(2)} with Card`}
            </Button>
          </div>
        </Card>
      )}

      {/* My Orders List */}
      <div>
        <h3 className="mb-4 text-lg font-bold text-slate-900">My Orders</h3>

        {myOrders.length === 0 ? (
          <Card className="rounded-2xl p-12 text-center">
            <Package className="mx-auto mb-3 h-12 w-12 text-slate-300" />
            <p className="text-slate-500">No orders yet.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {myOrders.map((order) => (
              <Card key={order.id} className="rounded-2xl p-6 shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-semibold text-slate-900">{order.productName}</p>
                    {order.quantity > 1 && (
                      <p className="text-xs text-slate-500">Qty: {order.quantity}</p>
                    )}
                    <p className="text-xs text-slate-500">Order #{order.id.slice(-8)}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(order.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'long', year: 'numeric'
                      })}
                    </p>
                  </div>
                  {getStatusBadge(order.status)}
                </div>

                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <div className="flex justify-between mb-1">
                    <span className="text-slate-600">Product{order.quantity > 1 ? ` × ${order.quantity}` : ''}</span>
                    <span className="font-semibold">£{(order.productPrice || 0).toFixed(2)}</span>
                  </div>
                  {order.shippingCost != null && (
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-600">Shipping</span>
                      <span className="font-semibold">£{(order.shippingCost || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-teal-600">£{(order.amount || order.total || 0).toFixed(2)}</span>
                  </div>
                </div>

                {/* Tracking information */}
                {order.trackingNumber && (
                  <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Truck className="h-4 w-4 text-blue-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-semibold text-blue-900">
                          {order.deliveryProvider ? `${order.deliveryProvider}: ` : 'Tracking: '}{order.trackingNumber}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
